import * as moment from 'moment';

import { VendorBasicModel, VendorStoreModel } from "./VendorModel";
import { CategoryModel, ThematicModel, ZoneModel } from './CategoryModel';
import { AudiencesType, Country, CurrencyType, EventFormatType, EventStatus } from './Enums';
import { EventType } from './Enums';
import { TicketModel } from './TicketModel';
import { CommunityModel, CommunityVendorModel } from './CommunityModel';
import { PackageVendorModel } from './PackageModel';
import { IBenefitModel } from './Interfaces';
import { CurrencyInfo } from '../currency-info';

export class EventModel implements IBenefitModel {
    
    public constructor(init?: Partial<Pick<EventModel, keyof EventModel>>) {

        if (init) {
            if (init.vendor) {
                init.vendor = new VendorBasicModel(init.vendor);
            }
            if (init.community) {
                init.community = new CommunityModel(init.community);
            }
            if (init.cashless && init.cashless.length) {
                init.cashless = init.cashless.map((item: any) => new CashlessModel(item));
            }
            if (init.tickets && init.tickets.length) {
                init.tickets = init.tickets.map((item: any) => new TicketModel(item));
            }
            if (typeof init.startDateAt === 'string') {
                init.startDateAt = new Date(init.startDateAt);
            }
            if (typeof init.endDateAt === 'string') {
                init.endDateAt = new Date(init.endDateAt);
            }
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.approvedAt === 'string') {
                init.approvedAt = new Date(init.approvedAt);
            }
            /*
            if (init.tickets && init.tickets.length) {
            
                init.tickets = init.tickets.map(x => new TicketModel(x));
            }*/
            Object.assign(this, init);
        }
    }

    id: number;
    vendorId: number;
    name: string;
    description: string;
    logoUrl: string;
    thumbnailUrl: string;
    terms: string;

    audiences = AudiencesType.AllPublic;
    format = EventFormatType.InPerson;
    zoneId: number;

    isGeneric = true;

    isLite = false;
    allowDownload = false;

    // Inventario total (si es manejado por el evento)
    stock: number;
    // Total vendido del inventario (si es manejado por el evento)
    quantity: number;

    // Inventario total de los tickets creados
    stockByTickets: number = 0;
    // Total vendidos del los tickets creados
    quantityByTickets: number = 0;

    email: string;
    phone: string;
    whatsappPhone: string;
    otherPhone: string;

    isActive: boolean;
    approvedAt: Date;
    // public DisabledType DisabledType { get; set; }
    disabledAt: Date;


    province = 0;
    canton = 0;
    district = 0;

    addressMap: string;

    addressDetails: string;

    wazeLink: string;

    googleMapLink: string;

    startDateAt: Date;
    endDateAt: Date;

    type: EventType;

    status: EventStatus;

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;

    country = Country.CR;


    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    vendor: VendorBasicModel;
    communityVendor: CommunityVendorModel;
    packageVendor: PackageVendorModel;
    communityId: number;

    community: CommunityModel;

    zone: ZoneModel;

    tickets: TicketModel[];

    cashless: CashlessModel[];

    categories: CategoryModel[] = [];

    thematics: ThematicModel[] = [];

    price: number; // Use IBenefitModel

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }

    // Use IBenefitModel
    get stores(): VendorStoreModel[] {

        let stores: VendorStoreModel[] = [];


        if (this.tickets.length > 0) {

            stores = this.tickets[0]?.stores;

        } else if (this.cashless.length > 0) {

            stores = this.cashless[0]?.stores;
        }

        return stores.filter(x => x.deletedAt == null);
    }

    get thematicName(): string {

        if (this.thematics?.length) {
            return this.thematics[0]?.name;
        }
        return null;
    }

    get thematicIcon(): string {

        if (this.thematics?.length) {
            return this.thematics[0]?.iconURL;
        }
        return null;
    }

    /**
     * Si el evento ha expirado o la cantidad comprada es mayor o igual que el stock, devuelve verdadero. 
     * De lo contrario, devuelve falso
     */
    get isOutOfStock(): boolean {

        // Si es genérico no tiene limite de cantidad
        if (this.isGeneric) {
            return false;
        }

        // Si el aforo es ilimitado
        if (this.stock == 0) {
            return false;
        }

        /* Creando un nuevo objeto Fecha. */
        const now = new Date;

        /* Agregando 4 horas a la hora actual. */
        now.setHours(now.getHours() + 4);

        /* Valida si el evento ha caducado. */
        const invalid = moment(this.endDateAt).isBefore(now);
        if (invalid) {
            return true;
        }

        // Valida si la cantidad comprada es mayor o igual al stock
        if (this.quantity >= this.stock) {
            return true;
        }

        /* Valida si la cantidad total de tickets creados es igual a la cantidad de tickets comprados*/
        /* Esto aplica solo para los tickets que no son de tipo genérico*/
        if (this.quantityByTickets >= this.stockByTickets && this.quantityByTickets != 0 && this.stockByTickets != 0) {
            return true;
        }

        return false;
    }

    get isOutOfStockText(): string {

        /* Creando un nuevo objeto Fecha. */
        const now = new Date;

        /* Agregando 4 horas a la hora actual. */
        now.setHours(now.getHours() + 4);

        /* Valida si el evento ha caducado. */
        const invalid = moment(this.endDateAt).isBefore(now);
        if (invalid) {
            return 'Expirado';
        }

        // Valida si la cantidad comprada es mayor o igual al stock
        if (this.quantity >= this.stock) {
            return 'Sold out';
        }

        /* Valida si la cantidad total de tickets creados es igual a la cantidad de tickets comprados*/
        /* Esto aplica solo para los tickets que no son de tipo genérico*/
        if (this.quantityByTickets >= this.stockByTickets && this.quantityByTickets != 0 && this.stockByTickets != 0) {
            return 'Sold out';
        }

        return 'Sold out';
    }

    get availableQuantity(): number {

        const quantity = this.stock - this.quantity;
        return quantity;
    }

    //show the crono 
    timeToReadyToRedeem: moment.Moment;
    readyToRedeemTimeText: string = "";

    get readyToRedeem(): boolean {

        if (this.isLite) return true;

        const now = new Date;
        const start = new Date(this.startDateAt);

        let dateNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let dateStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());

        if (dateNow < dateStart) {
            return false;
        }
        else {
            return true;
        }
    }

    get readyToDownload(): boolean {

        const now = new Date();
        const start = new Date(this.startDateAt);

        let dateNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let dateStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());

        // Restar 5 días a la fecha de inicio
        let thresholdDate = new Date(dateStart);
        thresholdDate.setDate(dateStart.getDate() - 5);

        return dateNow >= thresholdDate;
    }
}

export class CashlessModel extends EventModel {

    public constructor(init?: Partial<CashlessModel>) {
        super(init);
    }

    eventId: number;
    passId: number;

}